import React, { useEffect, useState } from 'react';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { Link } from '@uhg-abyss/web/ui/Link';
import { styled } from '@uhg-abyss/web/tools/styled';

const Wrapper = styled('div', {
  position: 'sticky',
  top: '24px',
  overflow: 'auto',
  paddingLeft: '30px',

  '@screen >= lg': {
    display: 'block',
  },
});

const Heading = styled('div', {
  color: '#5a5a5a',
  fontSize: '13px',
  fontWeight: '700',
  lineHeight: '16px',
  textAlign: 'left',
  textTransform: 'uppercase',
  margin: '8px 0px',
});

const StyledNavLink = styled('div', {
  display: 'block',
  marginBottom: '8px',
  'abyss-link-root': {
    fontSize: '15px !important',
  },
});

const StyledListItem = styled('li', {
  paddingLeft: '12px',
});

const StyledLink = styled(Link, {});

export const TableOfContents = () => {
  const [headings, setHeadings] = useState([]);
  const [subHeadings, setSubHeadings] = useState([]);
  const { resource, type } = useParams();

  const getSubheaderView = (id) => {
    const element = document.getElementById(id);
    element.scrollIntoView();
  };

  useEffect(() => {
    // Define a clean implementation for getting headings
    const getHeadingsWithCheck = () => {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Get main headings
        const elements = Array.from(document.querySelectorAll('div.tableof'))
          .filter((element) => {
            // Ensure element has ID and is visible
            return (
              element.id &&
              !element.classList.contains('hidden') &&
              getComputedStyle(element).display !== 'none' &&
              element.offsetParent !== null
            );
          })
          .map((element) => {
            return {
              id: element.id,
              text: element.textContent?.trim() ?? '',
            };
          });

        // Only update state if we found elements or need to clear previous list
        if (elements.length > 0 || headings.length > 0) {
          setHeadings(elements);
        }

        // Get sub headings
        const elementsSub = Array.from(
          document.querySelectorAll('div.elementof')
        )
          .filter((element) => {
            // Ensure element has ID and is visible
            return (
              element.id &&
              !element.classList.contains('hidden') &&
              getComputedStyle(element).display !== 'none' &&
              element.offsetParent !== null
            );
          })
          .map((element) => {
            return {
              id: element.id,
              text: element.textContent?.trim() ?? '',
            };
          });

        // Only update state if we found elements or need to clear previous list
        if (elementsSub.length > 0 || subHeadings.length > 0) {
          setSubHeadings(elementsSub);
        }
      });
    };

    // Set up a mutation observer to watch for content changes
    const observer = new MutationObserver((mutations) => {
      getHeadingsWithCheck();
    });

    // Initial attempt to get headings
    getHeadingsWithCheck();

    // Start observing the document body for content changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'id'],
    });

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, [resource, type, headings.length, subHeadings.length]);

  return (
    <Wrapper>
      <Heading>ON THIS PAGE</Heading>
      {headings.map((topic) => {
        return (
          <StyledNavLink key={topic.id}>
            <StyledLink
              size="md"
              css={{ fontSize: '15px !important', fontWeight: 400 }}
              onClick={() => {
                return getSubheaderView(topic.id);
              }}
            >
              {topic.text}
            </StyledLink>
            <ul>
              {subHeadings
                .filter((element) => {
                  return element.id.includes(topic.id);
                })
                .map((subheading) => {
                  return (
                    <StyledListItem key={subheading.id}>
                      <StyledLink
                        size="md"
                        css={{ fontSize: '15px !important', fontWeight: 400 }}
                        onClick={() => {
                          return getSubheaderView(subheading.id);
                        }}
                      >
                        {subheading.text}
                      </StyledLink>
                    </StyledListItem>
                  );
                })}
            </ul>
          </StyledNavLink>
        );
      })}
    </Wrapper>
  );
};
